import {Card, DataStatus} from 'platform/components';
import {VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';

import {
  AftersalesPaymentMethod,
  useDeleteCheckoutDepositPaymentMutation,
  useGetCheckoutByIdQuery,
  useGetCheckoutPaymentsV2Query,
  usePostCheckoutDepositPaymentCreateMutation,
  usePutCheckoutDepositPaymentAllowMutation,
  usePutCheckoutDepositPaymentDisallowMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {ContextTarget, RestrictionActionContext} from '../constants/issueRestrictionAlerts';
import {CheckoutPaymentCard} from './CheckoutPaymentCard';

interface DepositPaymentsCardProps extends RequiredTestIdProps {
  checkoutId: string;
  contextTarget?: ContextTarget;
  contextId?: string;
  actionContext?: RestrictionActionContext;
  defaultPaymentType?: AftersalesPaymentMethod;
  isEditingDisabled?: boolean;
  isCompletingDisabled?: boolean;
  isActivationDisabled?: boolean;
  serviceCaseId?: string;
  serviceOrderId?: string;
}

export function DepositPaymentsCard(props: DepositPaymentsCardProps) {
  const {
    data: checkoutData,
    isLoading: isCheckoutLoading,
    isError: isCheckoutError,
  } = useGetCheckoutByIdQuery({checkoutId: props.checkoutId});

  const {
    data: paymentsData,
    isLoading: isPaymentsLoading,
    isError: isPaymentsError,
  } = useGetCheckoutPaymentsV2Query({checkoutId: props.checkoutId});

  const [putCheckoutDepositPaymentAllow, {isLoading: isAllowingLoading}] =
    usePutCheckoutDepositPaymentAllowMutation();
  const [putCheckoutDepositPaymentDisallow, {isLoading: isDisallowingLoading}] =
    usePutCheckoutDepositPaymentDisallowMutation();
  const [postCheckoutDepositPaymentCreate] = usePostCheckoutDepositPaymentCreateMutation();
  const [deleteCheckoutDepositPayment] = useDeleteCheckoutDepositPaymentMutation();

  const balancePayment = paymentsData?.find((payment) => payment.discriminator === 'BALANCE');

  const handleSwitchDepositPayments = (shouldAllow: boolean) => {
    if (shouldAllow) {
      return putCheckoutDepositPaymentAllow({
        checkoutId: props.checkoutId,
        balancePaymentId: balancePayment?.checkoutPaymentId,
      })
        .unwrap()
        .catch(handleApiError);
    }

    return putCheckoutDepositPaymentDisallow({
      checkoutId: props.checkoutId,
      balancePaymentId: balancePayment?.checkoutPaymentId,
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleAddAnotherDeposit = () =>
    postCheckoutDepositPaymentCreate({checkoutId: props.checkoutId}).unwrap().catch(handleApiError);

  const handleRemoveDeposit = (paymentId: string) => {
    if (depositPayments?.length === 1) {
      return putCheckoutDepositPaymentDisallow({
        checkoutId: props.checkoutId,
        balancePaymentId: balancePayment?.checkoutPaymentId,
      })
        .unwrap()
        .catch(handleApiError);
    }

    return deleteCheckoutDepositPayment({checkoutId: props.checkoutId, paymentId})
      .unwrap()
      .catch(handleApiError);
  };

  const depositPayments = paymentsData?.filter((payment) => payment.discriminator === 'DEPOSIT');

  const isBalancePaymentIssued =
    balancePayment?.paymentState === 'PENDING' ||
    balancePayment?.paymentState === 'PARTIALLY_PAID' ||
    balancePayment?.paymentState === 'PAID';

  const hasSomePaidPayment =
    !!depositPayments?.length && depositPayments.some((payment) => payment.paymentState === 'PAID');

  const isLoading = isPaymentsLoading || isCheckoutLoading;

  const isError = isPaymentsError || isCheckoutError;

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <Card
        control={{
          type: 'switch',
          onChange: handleSwitchDepositPayments,
          value: !!checkoutData?.isDepositAllowed,
          isDisabled:
            isAllowingLoading ||
            isDisallowingLoading ||
            hasSomePaidPayment ||
            props.isEditingDisabled ||
            isBalancePaymentIssued ||
            props.isActivationDisabled,
          tooltip:
            props.isActivationDisabled && isNotNil(checkoutData)
              ? i18n.t('entity.checkout.labels.depositNotAllowedInVariantSettings')
              : undefined,
        }}
        title={i18n.t('entity.checkout.deposit')}
        variant="inlineGrey"
        isExpanded={checkoutData?.isDepositAllowed ?? false}
        isClosedByDefault
      >
        <VStack spacing={4}>
          {depositPayments?.map((payment, index) => {
            const isLastPayment = depositPayments.length - 1 === index;

            return (
              <CheckoutPaymentCard
                key={payment.checkoutPaymentId}
                paymentId={payment.checkoutPaymentId}
                checkoutId={props.checkoutId}
                customerId={checkoutData?.billingInformation?.customerInformation.id ?? ''}
                defaultPaymentType={props.defaultPaymentType}
                onAddAnotherDepositClick={
                  isLastPayment && !isBalancePaymentIssued ? handleAddAnotherDeposit : undefined
                }
                onRemoveDepositClick={() => handleRemoveDeposit(payment.checkoutPaymentId)}
                isBalancePaymentIssued={isBalancePaymentIssued}
                isEditingDisabled={props.isEditingDisabled}
                isCompletingDisabled={props.isCompletingDisabled}
                serviceCaseId={props.serviceCaseId}
                serviceOrderId={props.serviceOrderId}
                contextTarget={props.contextTarget}
                contextId={props.contextId}
                actionContext={props.actionContext}
                data-testid={suffixTestId(`depositPayment-${index}`, props)}
              />
            );
          })}
        </VStack>
      </Card>
    </DataStatus>
  );
}
